import {userStore} from '@js/stores/userStore';
import {validateUser} from '@js/actions/userActions';

import StorageService from '@js/modules/storage';
import Notification from '@js/components/layouts/notification';
import AnalyticsService from '@js/modules/analyticsService';

// Identifier-first lookup: does an account already exist for this email? Reuses the
// existing `/api/v1/users/validation` endpoint, which — for an existing email — also
// returns whether it is an external (OAuth-only) account and its linked providers, so
// the modal can steer the user to the right second step (password vs OAuth). No new
// enumeration surface: this endpoint already backs the signup form's email check.
export const lookupAccount = (email) => (
    validateUser({email})
        .then((response) => ({
            exists: !!(response && response.success),
            external: !!response?.external,
            providers: response?.providers || []
        }))
);

export const showAccountModal = () => {
    userStore.state.accountModal?.show();
};

export const hideAccountModal = () => {
    userStore.state.accountModal?.hide();
};

// One active gate at a time (only one account-required action can be pending in
// practice): opening a new gate cancels the previous pending one. Each call keeps its
// OWN unsubscribe in a closure, so a caller's returned canceller can never tear down a
// later gate's subscription.
let _cancelActive = null;

// Gate a free-account feature behind the account modal without a page reload.
//
// - If already connected, run `onConnected` immediately (no modal).
// - Otherwise open the account modal and run `onConnected` once the userStore flips to
//   connected (in-place email / OAuth popup). The business values stay captured in the
//   caller's closure.
//
// Options:
//   notify     — an info message shown before the modal (e.g. "log in to save").
//   resumeKey  — StorageService key persisting the intent across an in-app OAuth
//                full-page redirect; ONLY pass it when the call site rehydrates on mount
//                (e.g. RideCreation's SAVE_RESUME_KEY). Do not pass a key nothing reads.
//   onDismiss  — run when the modal is closed while still not connected (cleanup).
//
// Returns a canceller so component call sites can unsubscribe on unmount.
export const requireAccount = (feature, onConnected, {notify = null, resumeKey = null, onDismiss = null} = {}) => {
    if (userStore.isConnected) {
        onConnected?.();

        return () => undefined;
    }

    if (notify) {
        Notification.info(notify);
    }

    userStore.noRedirection = true;
    userStore.requiredFeature = feature || null;

    if (resumeKey) {
        StorageService.setItem(resumeKey, feature || true);
    }

    AnalyticsService.trackAccountGateShown(feature);

    // Cancel any previously-pending gate (single-flight) before starting this one.
    if (_cancelActive) {
        _cancelActive();
    }

    // Own unsubscribe kept in this closure (never the module var), so the canceller
    // returned to the caller can only ever tear down THIS gate's subscription.
    let unsubscribe = null;

    const cancel = () => {
        if (unsubscribe) {
            unsubscribe();
            unsubscribe = null;
        }

        if (_cancelActive === cancel) {
            _cancelActive = null;
        }
    };

    _cancelActive = cancel;

    unsubscribe = userStore.subscribe((state) => {
        if (state.isConnected && state.userId) {
            cancel();

            if (resumeKey) {
                StorageService.removeItem(resumeKey);
            }

            AnalyticsService.trackAccountGateSignup(feature);

            onConnected?.();
        }
    });

    userStore.state.accountModal?.updateOnHide(() => {
        if (!userStore.isConnected) {
            cancel();

            if (resumeKey) {
                StorageService.removeItem(resumeKey);
            }

            AnalyticsService.trackAccountGateDismissed(feature);

            onDismiss?.();
        }

        // Reset so the next open always changes requiredFeature and re-runs the modal's
        // step reset (even when the same feature is gated twice in a row).
        userStore.requiredFeature = null;
    });

    showAccountModal();

    return cancel;
};
